import type { Prospect, ProspectHarvestDetail } from '@prototypical/types';

export type HarvestPage = { hits: Prospect[]; nextCursor: string | null };
export type ExportScope = 'all' | 'selected';
export const MAX_EXPORT_BYTES = 100 * 1024 * 1024;
export class HarvestExportError extends Error {
  readonly payload: { error: string };
  constructor(message: string) { super(message); this.payload = { error: message }; }
}

export function checkAborted(signal: AbortSignal): void {
  if (signal.aborted) throw new DOMException('Cancelled', 'AbortError');
}

/** Finish every cursor page; a repeated cursor must never masquerade as a complete map. */
export async function readHarvestPages(
  readPage: (cursor?: string, signal?: AbortSignal) => Promise<HarvestPage>,
  signal: AbortSignal,
  onPage: (page: HarvestPage) => void,
  cursor?: string,
): Promise<void> {
  const seen = new Set<string>();
  do {
    checkAborted(signal);
    if (cursor && seen.has(cursor)) throw new Error('Saved harvest paging stalled. Refresh to try again.');
    if (cursor) seen.add(cursor);
    const page = await readPage(cursor, signal);
    checkAborted(signal);
    onPage(page);
    cursor = page.nextCursor ?? undefined;
  } while (cursor);
}

export function mergeProspects(previous: Prospect[], next: Prospect[]): Prospect[] {
  return [...new Map([...previous, ...next].map(prospect => [prospect.placeId, prospect])).values()];
}

/** Bound storage reads while preserving the scope's order. Any failure rejects the whole export. */
export async function collectHarvestDetails(
  prospects: Prospect[],
  readDetail: (id: string, signal?: AbortSignal) => Promise<ProspectHarvestDetail>,
  signal: AbortSignal,
  onProgress: (done: number) => void,
  maxBytes = MAX_EXPORT_BYTES,
): Promise<ProspectHarvestDetail[]> {
  const result = new Array<ProspectHarvestDetail>(prospects.length);
  let next = 0;
  let done = 0;
  let failed = false;
  let bytes = 0;
  await Promise.all(Array.from({ length: Math.min(3, prospects.length) }, async () => {
    while (next < prospects.length && !failed) {
      checkAborted(signal);
      const index = next++;
      try {
        result[index] = await readDetail(prospects[index].placeId, signal);
        checkAborted(signal);
        bytes += new Blob([JSON.stringify(result[index])]).size;
        if (bytes > maxBytes) throw new HarvestExportError('This export exceeds 100 MiB. Select fewer businesses and download them in batches.');
        if (!failed) onProgress(++done);
      } catch (error) { failed = true; throw error; }
    }
  }));
  checkAborted(signal);
  return result;
}

// Spreadsheet programs interpret formula prefixes even after leading whitespace.
export function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  const safe = /^[\s\u0000-\u001f]*[=+\-@]/.test(text) || /^[\t\r\n]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function harvestExport(details: ProspectHarvestDetail[], format: 'csv' | 'json', scope: ExportScope): Blob {
  if (format === 'json') return boundedExport(new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), scope, harvests: details }, null, 2)], { type: 'application/json;charset=utf-8' }));
  const columns = ['placeId', 'name', 'address', 'phone', 'internationalPhone', 'emails', 'openingHours', 'photoCount', 'menuItemCount', 'latitude', 'longitude', 'types', 'rating', 'reviewCount', 'website', 'harvestedAt', 'data', 'collections', 'photos'];
  const rows = details.map(detail => {
    const p = detail.prospect;
    const website = detail.collections.website?.find(row => row.id === 'current')?.data ?? {};
    const inlineWebsite = record(detail.data.websiteData);
    const hours = detail.collections.metadata?.find(row => row.id === 'hours')?.data.weekdayText ?? detail.data.openingHours;
    const emails = [...new Set([...strings(website.emails), ...strings(inlineWebsite.emails), ...strings(detail.data.emails)])].join('; ');
    return [p.placeId, p.name, p.address, detail.data.phone, detail.data.internationalPhone, emails, strings(hours).join('; '), detail.photos.length, detail.data.menuItemCount, p.location?.lat, p.location?.lng, JSON.stringify(p.types), p.rating, p.reviewCount, p.website, p.harvestedAt, JSON.stringify(detail.data), JSON.stringify(detail.collections), JSON.stringify(detail.photos)].map(csvCell).join(',');
  });
  return boundedExport(new Blob(['\uFEFF', columns.map(csvCell).join(','), '\r\n', rows.join('\r\n')], { type: 'text/csv;charset=utf-8' }));
}

function record(value: unknown): Record<string, unknown> { return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}; }
function strings(value: unknown): string[] { return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : typeof value === 'string' ? [value] : []; }
function boundedExport(blob: Blob): Blob {
  if (blob.size > MAX_EXPORT_BYTES) throw new HarvestExportError('This export exceeds 100 MiB. Select fewer businesses and download them in batches.');
  return blob;
}

export function saveDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = filename; document.body.appendChild(link); link.click(); link.remove();
  // Let the browser consume the click before releasing the download URL.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
